import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    FileText,
    DollarSign,
    Calendar,
    Upload,
    X,
    Info,
    Receipt,
    Loader,
    MapPin,
    Briefcase,
} from "lucide-react";
import {
    getMyNoteDeFraisRequestsRequest,
    createNoteDeFraisRequestRequest,
    clearNoteDeFraisRequestMessage,
} from "../../../../redux/actions/noteDeFraisRequestActions";
import { fetchNoteDeFraisRequest } from "../../../../redux/actions/noteDeFraisActions";
import "../MesrequetesStyles/MesAvances.css";

const ExpenseManagement = () => {
    const dispatch = useDispatch();

    // Redux state
    const { myRequests, loading } = useSelector((state) => state.noteDeFraisRequests);
    const { notesDeFrais } = useSelector((state) => state.noteDeFrais || { notesDeFrais: [] });
    const { ordresMissionActifs } = useSelector((state) => state.ordreMission || { ordresMissionActifs: [] });
    const user = useSelector((state) => state.auth.user);

    const [activeTab, setActiveTab] = useState("nouvelle");
    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    const [formData, setFormData] = useState({
        noteDeFraisId: "",
        category: "",
        ordreMissionId: "",
        dateDepense: "",
        montant: "",
        description: "",
        centreCouts: "",
        urgence: false,
        justificatifs: [],
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(fetchNoteDeFraisRequest());
        dispatch(getMyNoteDeFraisRequestsRequest());
        dispatch({ type: 'FETCH_ORDRE_MISSION_ACTIFS_REQUEST' }); // Charger les ordres de mission actifs
    }, [dispatch]);

    const getCouleurByCategorie = (categorie) => {
        const colors = {
            TRANSPORT: "#3b82f6",
            HEBERGEMENT: "#8b5cf6",
            RESTAURATION: "#10b981",
            AUTRE: "#f59e0b",
        };
        return colors[categorie] || "#6b7280";
    };

    // Calculer les stats du collaborateur
    const collaborateurStats = useMemo(() => {
        if (!user) return {
            totalMois: 0,
            totalAnnee: 0,
            demandesEnCours: 0,
            enAttenteRemboursement: 0,
        };

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        // Calculer le total du mois en cours
        const totalMois = myRequests
            .filter(req => {
                const date = new Date(req.dateDepense);
                return date.getMonth() === currentMonth &&
                    date.getFullYear() === currentYear &&
                    req.status === "APPROVED";
            })
            .reduce((sum, req) => sum + (req.montantDepense || 0), 0);

        // Calculer le total de l'année
        const totalAnnee = myRequests
            .filter(req => {
                const year = new Date(req.dateDepense).getFullYear();
                return year === currentYear && req.status === "APPROVED";
            })
            .reduce((sum, req) => sum + (req.montantDepense || 0), 0);

        // Compter les demandes en cours
        const demandesEnCours = myRequests.filter(
            req => req.status === "PENDING_MANAGER" || req.status === "PENDING_ADMIN"
        ).length;

        // Compter les demandes en attente de remboursement
        const enAttenteRemboursement = myRequests.filter(
            req => req.status === "APPROVED" && req.statutRemboursement === "EN_ATTENTE"
        ).length;

        return {
            totalMois,
            totalAnnee,
            demandesEnCours,
            enAttenteRemboursement,
        };
    }, [user, myRequests]);

    // Filtrer les types de notes de frais actifs
    const typesNoteDeFrais = useMemo(() => {
        return notesDeFrais
            .filter(ndf => ndf.actif)
            .map(ndf => ({
                id: ndf.id,
                nom: ndf.category,
                categorie: ndf.category,
                code: ndf.code,
                couleur: getCouleurByCategorie(ndf.category),
                montantMin: ndf.montantMinimum || 0,
                description: `Type de frais: ${ndf.typeJustificatif || 'Aucun justificatif'}`,
                workflow: ndf.workflow,
                centreCouts: ndf.centreCouts,
                justificatifsRequis: ndf.typeJustificatif ? [ndf.typeJustificatif] : [],
                delaiTraitement: "1-3 jours",
                obligatoire: ndf.obligatoire,
            }));
    }, [notesDeFrais]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant) => {
        if (!montant) return "0";
        return new Intl.NumberFormat("fr-MA", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(montant);
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files || []);
        setFormData((p) => ({
            ...p,
            justificatifs: [...p.justificatifs, ...files],
        }));
    };

    const removeFile = (index) => {
        setFormData((p) => ({
            ...p,
            justificatifs: p.justificatifs.filter((_, i) => i !== index),
        }));
    };

    const resetForm = () => {
        setFormData({
            noteDeFraisId: "",
            category: "",
            ordreMissionId: "",
            dateDepense: "",
            montant: "",
            description: "",
            centreCouts: "",
            urgence: false,
            justificatifs: [],
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const type = typesNoteDeFrais.find((t) => t.id === Number(formData.noteDeFraisId));
        if (!type) return;

        // Validation
        if (type.montantMin && Number(formData.montant) < type.montantMin) {
            alert(`Le montant doit être au moins de ${formatMontant(type.montantMin)} dhs`);
            return;
        }

        if (!formData.dateDepense) {
            alert("La date de dépense est obligatoire");
            return;
        }

        const dateDepense = new Date(formData.dateDepense);
        const today = new Date();
        if (dateDepense > today) {
            alert("La date de dépense ne peut pas être dans le futur");
            return;
        }

        if (!formData.description || formData.description.trim().length === 0) {
            alert("La description est obligatoire");
            return;
        }

        // Vérifier si des justificatifs sont requis
        if (type.obligatoire && formData.justificatifs.length === 0) {
            alert("Des justificatifs sont obligatoires pour ce type de frais");
            return;
        }

        // Préparer les données pour l'envoi
        const payload = {
            noteDeFraisId: Number(formData.noteDeFraisId),
            ordreMissionId: formData.ordreMissionId ? Number(formData.ordreMissionId) : null,
            dateDepense: formData.dateDepense,
            montantDepense: Number(formData.montant),
            description: formData.description,
            centreCouts: formData.centreCouts || null,
            urgent: formData.urgence,
            requestType: type.category || 'NOTE_FRAIS', // Type dérivé de la note de frais
            justificatifs: Array.isArray(formData.justificatifs) ? formData.justificatifs : [],
        };

        dispatch(createNoteDeFraisRequestRequest(payload));
        setShowModal(false);
        resetForm();

        // Passer à l'onglet historique après 1 seconde
        setTimeout(() => {
            setActiveTab("historique");
        }, 1000);
    };

    const getStatutBadge = (statut, demande = null) => {
        const config = {
            PENDING_MANAGER: { label: "En attente manager", cls: "maBadge maBadge--warn", Icon: Clock },
            PENDING_ADMIN: { label: "En attente RH", cls: "maBadge maBadge--warn", Icon: Clock },
            APPROVED: { label: "Approuvé", cls: "maBadge maBadge--ok", Icon: CheckCircle },
            REJECTED: { label: "Rejeté", cls: "maBadge maBadge--no", Icon: XCircle },
            PAID: { label: "Remboursé", cls: "maBadge maBadge--ok", Icon: CheckCircle },
            CANCELLED: { label: "Annulé", cls: "maBadge maBadge--no", Icon: XCircle },
        };

        // Pour les notes de frais approuvées mais non remboursées
        const d = demande || selectedDemande;
        if (statut === "APPROVED" && d?.statutRemboursement === "EN_ATTENTE") {
            return (
                <span className="maBadge maBadge--info">
                    <Clock size={14} />
                    En attente de remboursement
                </span>
            );
        }

        const c = config[statut] || { label: statut, cls: "maBadge", Icon: AlertCircle };
        const I = c.Icon;
        return (
            <span className={c.cls}>
                <I size={14} />
                {c.label}
            </span>
        );
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const openModal = (type) => {
        setFormData((p) => ({
            ...p,
            noteDeFraisId: type.id,
            category: type.nom,
            centreCouts: type.centreCouts || "",
        }));
        setShowModal(true);
    };

    const getWorkflowLabel = (workflow) => {
        const labels = {
            manager: "Manager → RH",
            admin: "Direct RH",
            both: "Manager et RH",
        };
        return labels[workflow] || workflow;
    };

    if (loading && myRequests.length === 0) {
        return (
            <div className="maPage">
                <div className="maWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="maPage">
            <div className="maWrap">
                {/* Header */}
                <div className="maHeader">
                    <h1>Mes notes de frais</h1>
                    <p>Déclarez vos frais professionnels et consultez vos remboursements</p>
                </div>

                {/* Stats */}
                <div className="maStats">
                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--accent">
                                <DollarSign size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">Total du mois</div>
                                <div className="maStatValue maStatValue--pri">
                                    {formatMontant(collaborateurStats.totalMois)} dhs
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--info">
                                <Calendar size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">Total de l'année</div>
                                <div className="maStatValue maStatValue--pri">
                                    {formatMontant(collaborateurStats.totalAnnee)} dhs
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--pri">
                                <Clock size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">En cours</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.demandesEnCours}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--ok">
                                <CheckCircle size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">À rembourser</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.enAttenteRemboursement}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card + Tabs */}
                <div className="maCard maCard--noPad">
                    <div className="maTabs">
                        <button
                            className={`maTab ${activeTab === "nouvelle" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("nouvelle")}
                        >
                            <Plus size={18} />
                            Nouvelle note de frais
                        </button>

                        <button
                            className={`maTab ${activeTab === "historique" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("historique")}
                        >
                            <FileText size={18} />
                            Historique
                            <span className={`maCount ${activeTab === "historique" ? "is-active" : ""}`}>
                                {myRequests.length}
                            </span>
                        </button>
                    </div>

                    <div className="maCardBody">
                        {/* Nouvelle demande */}
                        {activeTab === "nouvelle" && (
                            <>
                                <div className="maSectionHead">
                                    <div>
                                        <div className="maSectionTitle">Types de frais disponibles</div>
                                        <div className="maSectionSub">
                                            Sélectionnez le type de frais que vous souhaitez déclarer
                                        </div>
                                    </div>
                                </div>

                                {typesNoteDeFrais.length === 0 ? (
                                    <div className="maEmpty">
                                        <AlertCircle size={38} />
                                        <p>Aucun type de note de frais disponible pour le moment</p>
                                    </div>
                                ) : (
                                    <div className="maGrid2">
                                        {typesNoteDeFrais.map((type) => (
                                            <div
                                                key={type.id}
                                                className="maTypeCard is-eligible"
                                                style={{ borderColor: type.couleur }}
                                                onClick={() => openModal(type)}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <div className="maTypeTop">
                                                    <div className="maTypeLeft">
                                                        <div
                                                            className="maTypeIcon"
                                                            style={{
                                                                backgroundColor: `${type.couleur}20`,
                                                                color: type.couleur
                                                            }}
                                                        >
                                                            <Receipt size={18} />
                                                        </div>
                                                        <div>
                                                            <div className="maTypeName">{type.nom}</div>
                                                            <div
                                                                className="maPill"
                                                                style={{
                                                                    backgroundColor: `${type.couleur}20`,
                                                                    color: type.couleur
                                                                }}
                                                            >
                                                                {type.code}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="maTypeDesc">{type.description}</div>

                                                <div className="maTypeGrid">
                                                    <div>
                                                        <div className="maMuted">Montant min</div>
                                                        <div className="maStrong" style={{ color: type.couleur }}>
                                                            {formatMontant(type.montantMin)} dhs
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Workflow</div>
                                                        <div className="maStrong">
                                                            {getWorkflowLabel(type.workflow)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Centre coûts</div>
                                                        <div className="maStrong">{type.centreCouts || "—"}</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Traitement</div>
                                                        <div className="maStrong">{type.delaiTraitement}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="maInfoBox">
                                    <Info size={18} />
                                    <div>
                                        <div className="maInfoTitle">Informations importantes</div>
                                        <ul className="maInfoList">
                                            <li>Joignez les justificatifs originaux (factures, tickets)</li>
                                            <li>Les frais doivent être déclarés dans les 30 jours</li>
                                            <li>Le remboursement se fait sous 5 à 15 jours après validation</li>
                                            <li>Total du mois: <b>{formatMontant(collaborateurStats.totalMois)} dhs</b></li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Historique */}
                        {activeTab === "historique" && (
                            <>
                                {myRequests.length === 0 ? (
                                    <div className="maEmpty">
                                        <FileText size={38} />
                                        <p>Aucune note de frais enregistrée</p>
                                        <button
                                            className="maBtn maBtn--accent"
                                            onClick={() => setActiveTab("nouvelle")}
                                        >
                                            <Plus size={18} />
                                            Déclarer des frais
                                        </button>
                                    </div>
                                ) : (
                                    <div className="maHistory">
                                        {myRequests.map((d) => (
                                            <button
                                                key={d.id}
                                                className={`maHistoryCard ${d.urgent ? "is-urgent" : ""}`}
                                                onClick={() => showDetails(d)}
                                            >
                                                <div className="maHistoryTop">
                                                    <div className="maHistoryLeft">
                                                        <div
                                                            className="maHistoryIcon"
                                                            style={{
                                                                backgroundColor: `${getCouleurByCategorie(d.noteDeFrais?.category)}20`,
                                                                color: getCouleurByCategorie(d.noteDeFrais?.category)
                                                            }}
                                                        >
                                                            <Receipt size={18} />
                                                        </div>

                                                        <div>
                                                            <div className="maHistoryTitle">
                                                                {d.noteDeFrais?.category} {d.urgent && <span className="maFire">🔥</span>}
                                                            </div>
                                                            <div className="maSub">
                                                                Dépense du {formatDate(d.dateDepense)}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {getStatutBadge(d.status, d)}
                                                </div>

                                                <div className="maHistoryGrid">
                                                    <div>
                                                        <div className="maMuted">Montant</div>
                                                        <div className="maStrong maAccent">
                                                            {formatMontant(d.montantDepense)} dhs
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Validé</div>
                                                        <div className="maStrong">
                                                            {formatMontant(d.montantValide || d.montantDepense)} dhs
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Centre coûts</div>
                                                        <div className="maStrong">{d.centreCouts || "—"}</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Créé le</div>
                                                        <div className="maStrong">{formatDate(d.createdAt)}</div>
                                                    </div>
                                                </div>

                                                {d.description && (
                                                    <div className="maReject" style={{ background: "#f9fafb", border: "1px solid #e5e7eb", marginTop: 10 }}>
                                                        <div className="maMuted">Description</div>
                                                        <div style={{ color: "#374151", marginTop: 4, fontSize: 12 }}>
                                                            {d.description.length > 100
                                                                ? d.description.substring(0, 100) + "..."
                                                                : d.description}
                                                        </div>
                                                    </div>
                                                )}

                                                {d.status === "REJECTED" && (d.adminComment || d.managerComment) && (
                                                    <div className="maReject">
                                                        <div className="maMuted">Motif du rejet</div>
                                                        <div className="maRejectText">
                                                            {d.adminComment || d.managerComment}
                                                        </div>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL - Demande */}
            {showModal && (
                <div className="maModal">
                    <div className="maModal__panel">
                        <div className="maModal__header">
                            <div className="maModal__headLeft">
                                <div className="maModal__icon">
                                    <Receipt size={18} />
                                </div>
                                <div>
                                    <div className="maModal__title">Nouvelle note de frais</div>
                                    <div className="maModal__sub">{formData.category}</div>
                                </div>
                            </div>

                            <button
                                className="maIconBtn"
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                                aria-label="Fermer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="maModal__body">
                            <form onSubmit={handleSubmit}>
                                {formData.noteDeFraisId && (
                                    <div className="maInfoInline">
                                        {(() => {
                                            const type = typesNoteDeFrais.find((t) => t.id === Number(formData.noteDeFraisId));
                                            if (!type) return null;
                                            return (
                                                <div className="maInlineGrid">
                                                    <div>
                                                        <div className="maMuted">Montant min</div>
                                                        <div className="maStrong" style={{ color: type.couleur }}>
                                                            {formatMontant(type.montantMin)} dhs
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Workflow</div>
                                                        <div className="maStrong">
                                                            {getWorkflowLabel(type.workflow)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Centre coûts</div>
                                                        <div className="maStrong">{type.centreCouts || "—"}</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Traitement</div>
                                                        <div className="maStrong">{type.delaiTraitement}</div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                <div className="maFormGrid">
                                    <div className="maField">
                                        <label>
                                            Date de la dépense <span className="maReq">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.dateDepense}
                                            onChange={(e) => setFormData((p) => ({ ...p, dateDepense: e.target.value }))}
                                            max={new Date().toISOString().split('T')[0]}
                                            required
                                        />
                                        <div className="maHelp">
                                            La date ne peut pas être dans le futur
                                        </div>
                                    </div>

                                    <div className="maField">
                                        <label>
                                            Montant (dhs) <span className="maReq">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.montant}
                                            onChange={(e) => setFormData((p) => ({ ...p, montant: e.target.value }))}
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                        <div className="maHelp">
                                            Minimum:{" "}
                                            {formatMontant(
                                                typesNoteDeFrais.find((t) => t.id === Number(formData.noteDeFraisId))?.montantMin || 0
                                            )}{" "}
                                            dhs
                                        </div>
                                    </div>
                                </div>

                                <div className="maFormGrid">
                                    <div className="maField">
                                        <label>Ordre de mission (optionnel)</label>
                                        <select
                                            value={formData.ordreMissionId}
                                            onChange={(e) => setFormData((p) => ({ ...p, ordreMissionId: e.target.value }))}
                                        >
                                            <option value="">Aucun ordre de mission</option>
                                            {ordresMissionActifs.map((om) => (
                                                <option key={om.id} value={om.id}>
                                                    {om.libelle} - {new Date(om.dateDebut).toLocaleDateString('fr-FR')} au {new Date(om.dateFin).toLocaleDateString('fr-FR')}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="maHelp">
                                            Si cette dépense est liée à une mission spécifique
                                        </div>
                                    </div>

                                    <div className="maField">
                                        <label>Centre de coûts (optionnel)</label>
                                        <input
                                            type="text"
                                            value={formData.centreCouts}
                                            onChange={(e) => setFormData((p) => ({ ...p, centreCouts: e.target.value }))}
                                            placeholder="Ex: Marketing, IT, Ventes..."
                                        />
                                        <div className="maHelp">
                                            Laissez vide pour utiliser le centre par défaut
                                        </div>
                                    </div>
                                </div>

                                <div className="maField">
                                    <label>
                                        Description <span className="maReq">*</span>
                                    </label>
                                    <textarea
                                        rows={4}
                                        value={formData.description}
                                        onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                                        placeholder="Décrivez la nature de la dépense (lieu, objet, contexte...)"
                                        required
                                    />
                                    <div className="maHelp">
                                        Soyez précis pour faciliter la validation
                                    </div>
                                </div>

                                <div className="maCheckRow">
                                    <label className="maCheck">
                                        <input
                                            type="checkbox"
                                            checked={formData.urgence}
                                            onChange={(e) => setFormData((p) => ({ ...p, urgence: e.target.checked }))}
                                        />
                                        <span>Marquer comme urgent</span>
                                    </label>
                                    <span className="maHelp">Les demandes urgentes sont traitées en priorité</span>
                                </div>

                                <div className="maField">
                                    <label>
                                        Justificatifs
                                        {typesNoteDeFrais.find((t) => t.id === Number(formData.noteDeFraisId))?.obligatoire && (
                                            <span className="maReq">*</span>
                                        )}
                                    </label>

                                    <div className="maDrop">
                                        <Upload size={28} />
                                        <div className="maDropText">Glissez vos justificatifs ici ou cliquez pour parcourir</div>
                                        <input id="maFile" type="file" multiple onChange={handleFileUpload} hidden />
                                        <label className="maBtn maBtn--outline" htmlFor="maFile">
                                            Parcourir
                                        </label>
                                    </div>

                                    {formData.justificatifs.length > 0 && (
                                        <div className="maFiles">
                                            {formData.justificatifs.map((file, index) => (
                                                <div key={index} className="maFileRow">
                                                    <span>{file.name || file}</span>
                                                    <button
                                                        type="button"
                                                        className="maFileX"
                                                        onClick={() => removeFile(index)}
                                                        aria-label="Supprimer"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="maHelp" style={{ marginTop: 8 }}>
                                        Documents requis:{" "}
                                        {(typesNoteDeFrais.find((t) => t.id === Number(formData.noteDeFraisId))?.justificatifsRequis || []).join(", ") || "Factures, tickets, reçus"}
                                    </div>
                                </div>
                                <div className="maModal__footer">
                                    <button
                                        type="button"
                                        className="maBtn maBtn--ghost"
                                        onClick={() => {
                                            setShowModal(false);
                                            resetForm();
                                        }}
                                    >
                                        Annuler
                                    </button>
                                    <button type="submit" className="maBtn maBtn--accent" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <Loader size={16} className="spin" />
                                                Envoi en cours...
                                            </>
                                        ) : (
                                            "Soumettre"
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default ExpenseManagement;